import {
  ApplicationQuestionFormCheckboxField,
  ApplicationQuestionFormCurrencyField,
  ApplicationQuestionFormDateField,
  ApplicationQuestionFormNumberField,
  ApplicationQuestionFormSelectField,
  ApplicationQuestionFormTextField,
  ApplicationQuestionFormToggleField,
  ApplicationQuestionLabel,
  ApplicationQuestionMultilineFormTextField,
} from './application-question-form-fields';
import { FormFieldContainer } from '../../../presentation';
import { useFormContext, useWatch } from 'react-hook-form';

import { Divider } from '@mui/material';
import { QuestionType } from '@converge/graphql';
import { SubmissionApplicationQuestionDetailsExt } from './application-question';
import { handleError } from '@converge/utils';

import './application-question-switcher.scss';

interface IApplicationQuestionSwitcherProps {
  question: SubmissionApplicationQuestionDetailsExt;
  isApplicationViewOnly: boolean;
  onBlur?: () => void;
  onChange?: (key: string, value: unknown) => void;
  good?: boolean;
  startNumber?: number;
  isPrimary?: boolean;
  setRaterError?: boolean;
}

const ApplicationQuestionSwitcher = ({
  question,
  startNumber,
  isPrimary,
  question: {
    isGroup,
    isGroupItem,
    isSubGroupItem,
    groupChildren,
    children,
    subGroupChildren,
    isSubGroup,
    uuid,
  },
  isApplicationViewOnly,
  good,
  onBlur,
  onChange,
  setRaterError,
}: IApplicationQuestionSwitcherProps) => {
  const { control } = useFormContext();

  const isEnabled = useWatch({
    control,
    name: uuid,
  });

  const showChildQuestions = isGroup
    ? question?.type?.toUpperCase() === QuestionType.Bool
      ? isEnabled
      : true
    : false;

  const inner = (
    <div
      className={
        question?.level > 1 ? 'inner-question-wrapper' : 'question-wrapper'
      }
    >
      <ApplicationQuestionSwitcherInner
        question={question}
        isApplicationViewOnly={isApplicationViewOnly}
        onBlur={onBlur}
        onChange={onChange}
        // if question does not exist it use default true
        good={question?.good ? question?.good : true}
        isPrimary={isPrimary}
        setRaterError={setRaterError}
      />
      {
        // showChildQuestions &&
        <>
          {children?.map((q) => (
            <ApplicationQuestionSwitcher
              good={good}
              question={q}
              isApplicationViewOnly={isApplicationViewOnly}
              key={q.uuid}
              onBlur={onBlur}
              onChange={onChange}
              isPrimary={isPrimary}
            />
          ))}
        </>
        // ) : config.featureFlags.showQuestionsNotApplicableMessage &&
        //   groupChildren.length > 0 ? (
        // <FormFieldContainer md={12} textAlign={'center'}>
        //   <Typography color={'GrayText'}>
        //     {groupChildren.length
        //       ? `${groupChildren.length} question${
        //           groupChildren.length > 1 ? 's' : ''
        //         } not applicable`
        //       : ''}
        //   </Typography>
        // </FormFieldContainer>
        // ):// : null}
      }
      {/* 
      {children?.length > 0 && (
        <FormContainer pt={3}>
          {children.map((q) => (
            <ApplicationQuestionSwitcher
              question={q}
              good={good}
              isApplicationViewOnly={isApplicationViewOnly}
              key={q.uuid}
              isPrimary={isPrimary}
            />
          ))}
        </FormContainer>
      )} */}
      {showChildQuestions && isGroup && children?.length > 0 && (
        <FormFieldContainer md={12}>
          <Divider className="child-questions-divider" />
        </FormFieldContainer>
      )}
    </div>
  );

  if (isGroupItem || isSubGroupItem || isSubGroup) {
    return <FormFieldContainer ml={'1.5em'}>{inner}</FormFieldContainer>;
  }

  return <>{inner}</>;
};

interface IApplicationQuestionSwitcherInnerProps {
  question: SubmissionApplicationQuestionDetailsExt;
  isApplicationViewOnly: boolean;
  isGroupItem?: boolean;
  isSubGroupItem?: boolean;
  onBlur?: () => void;
  onChange?: (key: string, value: unknown) => void;
  good: boolean;
  isPrimary?: boolean;
  setRaterError?: boolean;
}

const ApplicationQuestionSwitcherInner = ({
  question,
  isApplicationViewOnly,
  onBlur,
  onChange,
  good,
  isPrimary,
  setRaterError,
}: IApplicationQuestionSwitcherInnerProps) => {
  switch (question?.type?.toUpperCase()) {
    case QuestionType.Label:
      return (
        <ApplicationQuestionLabel
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
        />
      );
    case QuestionType.String:
      return (
        <ApplicationQuestionFormTextField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
          isPrimaryEndorsement={isPrimary}
        />
      );
    case QuestionType.Float:
      return (
        <ApplicationQuestionFormNumberField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
          startNumber={1}
          isPrimaryEndorsement={isPrimary}
        />
      );
    case QuestionType.Int:
      return (
        <ApplicationQuestionFormNumberField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
          startNumber={1}
          isPrimaryEndorsement={isPrimary}
        />
      );
    case QuestionType.Currency:
      return (
        <ApplicationQuestionFormCurrencyField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
        />
      );
    case QuestionType.Bool:
      return (
        <ApplicationQuestionFormToggleField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
          good={question.good ? question.good : true}
        />
      );
    case QuestionType.Option:
      return (
        <ApplicationQuestionFormCheckboxField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
        />
      );

    case QuestionType.Multiline:
      return (
        <ApplicationQuestionMultilineFormTextField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
        />
      );
    case QuestionType.List:
      return (
        <ApplicationQuestionFormSelectField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
        />
      );
    case QuestionType.Date:
      return (
        <ApplicationQuestionFormDateField
          question={question}
          isDisabled={isApplicationViewOnly}
          onBlur={onBlur}
          onChange={onChange}
          isPrimaryEndorsement={isPrimary}
          setRaterError={setRaterError}
        />
      );
    // case QuestionType.Month:
    //   return (
    //     <ApplicationQuestionFormMonthField
    //       question={question}
    //       isDisabled={isApplicationViewOnly}
    //     />
    //   );
    default:
      handleError({
        message: `Unsupported question type: ${question.type}`,
        source: 'ApplicationQuestionSwitcherInner',
        context: {
          data: {
            question,
          },
        },
      });
      return <>TODO: support "{question.type}"</>;
  }
};

export default ApplicationQuestionSwitcher;
